'use client';

import { Lock } from 'lucide-react';

import { useCallback, useMemo } from 'react';

import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES } from '@/shared/constants/navigation';
import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { openUrlInNewTab } from '@/shared/utils/window';

import { cn } from '@/lib/utils';

import { LockedOverlayProps } from './types';

/**
 * Overlay component that blurs content and shows upgrade CTA for non-Business users.
 * - Non-admins on admin-only content (isAdminOnly): "Only visible to admins", no upgrade link
 */
export function LockedOverlay({
	children,
	isLocked,
	isAdminOnly,
	className,
	overlayClassName,
	lockedDescription,
	requiredPlanLabel,
	analytics,
}: LockedOverlayProps) {
	const selectedWorkspace = useAppStore((state) => state.selectedWorkspace);
	const isAdmin = selectedWorkspace?.currentUserRole === 'ADMIN';

	const handleUpgradeClick = useCallback(() => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_UPGRADE, {
			feature: analytics?.feature ?? MIXPANEL_APP_FEATURES.ANALYTICS,
			source: analytics?.source ?? (isAdminOnly ? 'workspace_analytics' : 'form_analytics'),
			user_role: isAdmin ? 'admin' : 'member',
			button_location: analytics?.buttonLocation ?? 'ANALYTICS',
		});
		openUrlInNewTab(APP_ROUTES.PLANS);
	}, [isAdminOnly, isAdmin, analytics]);

	const copy = useMemo(() => {
		// Workspace analytics are admin-only: members don't have access to all forms,
		// so aggregated workspace-level data doesn't make sense for them.
		// "Paid feature" would be false for a member on a paid plan, so the role lock
		// names the role instead.
		if (!isAdmin && isAdminOnly) {
			return {
				title: COPY.access.adminOnly,
				description: null,
				showUpgradeLink: false,
			};
		}
		return {
			title: COPY.plans.analyticsLocked(requiredPlanLabel),
			description: lockedDescription ?? COPY.plans.analyticsLockedDescription,
			showUpgradeLink: true,
		};
	}, [isAdmin, isAdminOnly, requiredPlanLabel, lockedDescription]);

	if (!isLocked) {
		return <>{children}</>;
	}

	return (
		// `overflow-hidden` is load-bearing: `blur-sm` spreads pixels beyond the content
		// box, so without clipping the blurred edges leak past the scrim at the corners.
		<div className={cn('relative overflow-hidden rounded-lg', className)}>
			{/* Blurred content */}
			<div className="pointer-events-none select-none blur-sm">{children}</div>

			{/* Overlay */}
			<div
				className={cn(
					'absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-background/60 backdrop-blur-[2px]',
					overlayClassName
				)}
			>
				<div className="flex flex-col items-center gap-3 text-center">
					<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
						<Lock className="h-5 w-5 text-muted-foreground" />
					</div>
					<div>
						<p className="text-sm font-medium">{copy.title}</p>
						{(copy.showUpgradeLink || copy.description) && (
							<p className="text-xs text-muted-foreground">
								{copy.showUpgradeLink ? (
									<>
										<button
											onClick={handleUpgradeClick}
											className="cursor-pointer border-b border-dashed border-muted-foreground p-0 transition-colors hover:border-primary hover:text-primary"
										>
											{COPY.plans.upgradeCta}
										</button>{' '}
										{copy.description}
									</>
								) : (
									copy.description
								)}
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
